import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';

import { TokenDetails } from './models/auth/token-details.type';
import { BreadCrumbService } from './services/bread-crumb/bread-crumb.service';
import { CubeService } from './services/cube/cube.service';
import { IdbService } from './services/storage/idb.service';
import { STORAGE_KEYS } from './services/storage/storage.service';
import { ThemeService } from './services/theme/theme.service';
import { AuthActions } from './store/actions/auth.actions';
import {
  selectTokenDetails,
  selectUserTenants,
} from './store/selectors/auth.selectors';
import { AppState } from './store/state/app.state';

import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { PrimeNG } from 'primeng/config';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cube-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject<void>();
  isDarkTheme: boolean;

  private subscriptions: Subscription[] = [];

  constructor(
    private breadCrumbService: BreadCrumbService,
    private cubeService: CubeService,
    private idbService: IdbService,
    private overlayContainer: OverlayContainer,
    private primeNg: PrimeNG,
    private renderer: Renderer2,
    private themeService: ThemeService,
    private readonly store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.breadCrumbService.setEventListeners();

    this.subscriptions.push(
      this.themeService.isDarkTheme.subscribe((isDark) => {
        this.isDarkTheme = isDark;
        this.overlayContainer
          .getContainerElement()
          .classList.remove(isDark ? 'light-theme' : 'dark-theme');
        this.overlayContainer
          .getContainerElement()
          .classList.add(!isDark ? 'light-theme' : 'dark-theme');

        this.primeNg.overlayOptions = {
          styleClass: isDark ? 'dark-theme' : 'light-theme',
        };
      })
    );

    fromEvent(window, 'storage')
      .pipe(filter((ev: StorageEvent) => ev.key === STORAGE_KEYS.currentTenant))
      .subscribe((storageEvent: StorageEvent) => {
        this.cubeService.cleanCache();
        window.location.reload();
      });

    this.primeNg.ripple.set(true);
    // TEMP: For B2C migration
    // INFO Moved this code to cube component
    // this.addAllOngoingReportJobsToWorker();
    // this.addAllOngoingImportJobsToWorker();
    this.renderer.setStyle(document.body, 'overscroll-behavior-y', 'contain');
    this.renderer.setStyle(document.body, 'overscroll-behavior-x', 'contain');

    // Setup indexedDB
    this.idbService.initializeDatabase();

    this.store
      .select(selectTokenDetails)
      .pipe(
        filter(
          (
            tokenDetails: TokenDetails | undefined
          ): tokenDetails is TokenDetails => !!tokenDetails
        ),
        concatLatestFrom(() => this.store.select(selectUserTenants)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ([tokenDetails, tenants]) => {
          // TEMP: For B2C migration
          //INFO - Update token after retrieving it for use in the web worker.
          this.cubeService.updateToken(tokenDetails.idToken);
          if (!tenants.length) {
            this.store.dispatch(AuthActions.getUserTenants({ tokenDetails }));
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  getRouteAnimation(outlet) {
    return outlet.activatedRouteData.animation;
  }

  // TEMP: For B2C migration
  // INFO Moved this code to cube component
  // private addAllOngoingReportJobsToWorker(): void {
  //   this.reportService.addAllOngoingReportJobsToWorker();
  // }
  // private addAllOngoingImportJobsToWorker(): void {
  //   this.importService.addAllOngoingImportJobsToWorker();
  // }
}
